import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../api";

/**
 * Every sync this page has had, newest first.
 *
 * The outbound push is a full overwrite, so when a client edit goes missing the
 * only useful question is "what ran, and what did it do?". This answers it:
 * pushes, pushes that stood down because the tab held unreviewed edits, checks,
 * and each approve/reject/restore, with who triggered it.
 */
const LABELS = {
  "out.pushed": { text: "Pushed to the sheet", cls: "diff-add" },
  "out.forced": { text: "Overwrote the sheet", cls: "diff-update" },
  "out.blocked": { text: "Push held back", cls: "diff-update" },
  "out.skipped": { text: "Push skipped", cls: "" },
  "out.error": { text: "Push failed", cls: "diff-del" },
  "in.checked": { text: "Checked the sheet", cls: "" },
  "in.approved": { text: "Changes approved", cls: "diff-add" },
  "in.rejected": { text: "Changes rejected", cls: "diff-del" },
  "in.restored": { text: "Snapshot restored", cls: "diff-update" },
  "in.resynced": { text: "Re-synced by hand", cls: "diff-update" },
};

const when = (iso) => {
  if (!iso) return "";
  // Server timestamps are UTC without a zone marker; say so before parsing.
  const d = new Date(/[zZ+]/.test(iso) ? iso : `${iso}Z`);
  return isNaN(d) ? iso : d.toLocaleString();
};

export default function SyncLogModal({ open, sheetId, onClose }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    (async () => {
      try {
        const { data } = await api.get(`/sheets/${sheetId}/sync-log`);
        if (!cancelled) setEvents(data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Couldn't load the sync log");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, sheetId]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-card inbound-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{ maxWidth: 680 }}
      >
        <h3>Sync log</h3>
        <p className="muted" style={{ marginTop: 8 }}>
          Everything that has passed between this milestone and its Google tab,
          newest first. A push that found unreviewed edits in the tab is listed as
          held back: it left the client's changes alone instead of overwriting them.
        </p>

        {loading && <div className="muted" style={{ marginTop: 14 }}>Loading…</div>}
        {err && <div className="error" style={{ marginTop: 14 }}>{err}</div>}

        {!loading && !err && events.length === 0 && (
          <p className="muted-sm" style={{ marginTop: 14 }}>
            Nothing recorded yet.
          </p>
        )}

        {events.length > 0 && (
          <div className="inbound-body" style={{ marginTop: 10 }}>
            {events.map((e) => {
              const label = LABELS[e.action] || { text: e.action, cls: "" };
              return (
                <div className={`diff-row ${label.cls}`} key={e.id} style={{ marginBottom: 6 }}>
                  <div className="diff-head">
                    <span className="diff-name">{label.text}</span>
                    <span className="muted-sm" style={{ marginLeft: "auto" }}>
                      {when(e.created_at)}
                    </span>
                  </div>
                  <div className="muted-sm">
                    {e.rows !== null && e.rows !== undefined && <>{e.rows} rows</>}
                    {e.rows !== null && e.rows !== undefined && e.detail && " · "}
                    {e.detail}
                    {e.actor_name && <> · {e.actor_name}</>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="row-h" style={{ justifyContent: "flex-end", marginTop: 20 }}>
          <button type="button" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>,
    document.body
  );
}
